import SearchTextField from "components/common/InputField/SearchTextField";
import React, { useState } from "react";
import type { SpaceMessage as SpaceMessageType } from "types/spaceTypes";
import SpaceMessageAvatar from "./SpaceMessage/SpaceMessageAvatar";
import { useParams } from "react-router-dom";
import useGetUserProfile from "hooks/useGetUserProfile";
import { useAppSelector } from "hooks/storeHooks";

type SpaceDetailsPinnedMessagesSidebarProps = {
    pinnedMessages?: SpaceMessageType[];
    onUnpinMessage?: (messageId: string) => void;
    onJumpToMessage?: (messageId: string) => void;
    currentUserId?: string;
};
export default function SpaceDetailsPinnedMessagesSidebar(
    props: SpaceDetailsPinnedMessagesSidebarProps
) {
    const { pinnedMessages = [], onJumpToMessage, onUnpinMessage, currentUserId } = props;

    const { spaceId } = useParams();

    const [searchTerm, setSearchTerm] = useState("");

    // Filter pinned messages based on search term
    const filteredPinnedMessages = pinnedMessages.filter(
        (message) =>
            message.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
            message.author.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
    };

    const handleMessageClick = (messageId: string) => {
        onJumpToMessage?.(messageId);
    };


    return (
        <div className="h-full overflow-hidden">
            <p className="text-sm font-rocGroteskMedium font-semibold px-[13px] py-[19px]">
                Pinned chat on this spaces
            </p>

            <SearchTextField
                inputPlaceholder="Search Pins"
                inputContainerClassname="border-b !border-l-0 !border-r-0 !border-slate-100 !rounded-none !z-0 !overflow-hidden"
                value={searchTerm}
                onChange={handleSearchChange}
            />

            <div className="h-full overflow-y-scroll overflow-x-hidden scrollbar-hidden">
                {filteredPinnedMessages.length > 0 ? (
                    filteredPinnedMessages.map((message) => (
                        <div
                            key={message.id}
                            className="flex w-full items-start gap-2 p-[12px] hover:bg-slate-100 px-[16px] py-[12px] cursor-pointer transition-colors"
                            onClick={() => handleMessageClick(message.id)}
                        >
                            <SpaceMessageAvatar author={message.author} size="sm" />
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-rocGroteskMedium font-semibold">
                                    {message.author.name}
                                </p>
                                <p className="text-sm font-rocGroteskRegular text-slate-500 line-clamp-2 break-words">
                                    {message.content}
                                </p>

                                <div className="flex items-center gap-1 text-[#0054C2] mt-1">
                                    <i className="ri-pushpin-fill text-lg"></i>
                                    <p className="text-sm font-rocGroteskRegular">
                                        Pinned by{" "}
                                        {(() => {
                                            const pinnedByUserId = message.pinnedBy;
                                            const pinnedByUserName = message.author.name; // Fallback to message author

                                            // If we have the current user ID and it matches who pinned it
                                            if (currentUserId && pinnedByUserId === currentUserId) {
                                                return "you";
                                            }

                                          
                                            return pinnedByUserName;
                                        })()}
                                    </p>
                                </div>

                                {/* Unpin button - only show if current user pinned it and it's not an AI message */}
                                {currentUserId && 
                                 message.pinnedBy === currentUserId && 
                                 message.author.id !== "synth" && 
                                 message.author.name !== "Synth" && 
                                 onUnpinMessage && (
                                    <button
                                        onClick={() => onUnpinMessage(message.id)}
                                        className="p-1 rounded hover:bg-gray-200 transition-colors"
                                        title="Unpin message"
                                    >
                                        <i className="ri-unpin-line text-gray-500 text-sm"></i>
                                    </button>
                                )}
                            </div>
                        </div>
                    ))
                ) : pinnedMessages.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-32 text-gray-500">
                        <i className="ri-pushpin-line text-3xl mb-2"></i>
                        <p className="text-sm">No pinned messages yet</p>
                        <p className="text-xs text-gray-400">Pin messages to see them here</p>
                    </div>
                ) : (
                    <div className="flex flex-col items-center justify-center h-32 text-gray-500">
                        <i className="ri-search-line text-3xl mb-2"></i>
                        <p className="text-sm">No matching pinned messages</p>
                    </div>
                )}
            </div>
        </div>
    );
}
